import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StoryCard from '@/components/base/StoryCard';
import Tag from '@/components/base/Tag';
import useSeo from '@/hooks/useSeo';
import { getCategoryBySlug } from '@/mocks/categories';
import { getRelatedStories, getStoryById } from '@/mocks/stories';

const levelTone: Record<string, 'primary' | 'accent' | 'secondary' | 'neutral'> = {
  已确认: 'secondary',
  多方印证: 'accent',
  存疑: 'primary',
  暂无法确认: 'neutral',
};

const sections = [
  { id: 'content', label: '事件梳理' },
  { id: 'timeline', label: '时间线' },
  { id: 'personas', label: '人物与账号' },
];

export default function MelonPage() {
  const { id = '' } = useParams();
  const story = getStoryById(id);
  const [bookmarked, setBookmarked] = useState(false);
  const [copied, setCopied] = useState(false);

  const category = story ? getCategoryBySlug(story.categorySlug) : undefined;
  const related = story ? getRelatedStories(story) : [];

  useSeo({
    title: story ? `${story.title}｜西瓜瓜田 线索档案` : '瓜料未找到｜西瓜瓜田',
    description: story
      ? `${story.excerpt} 当前判定：${story.verdictLevel}。`
      : '这条瓜料可能已经下架或更名，返回首页查看最新的娱乐线索。',
    keywords: story
      ? `${story.tags.join(', ')}, ${story.category}, 西瓜黑料, 西瓜黑料小号`
      : '西瓜瓜田, 娱乐吃瓜',
    path: story ? `/melon/${story.id}` : '/',
    type: story ? 'article' : 'website',
    jsonLd: story
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: story.title,
            description: story.excerpt,
            datePublished: story.date,
            dateModified: story.date,
            articleSection: story.category,
            keywords: story.tags.join(','),
            author: { '@type': 'Organization', name: story.author },
            publisher: { '@type': 'Organization', name: '西瓜瓜田' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xiguaguatian.example.com/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: story.category,
                item: `https://xiguaguatian.example.com/category/${story.categorySlug}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: story.title,
                item: `https://xiguaguatian.example.com/melon/${story.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!story) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto flex min-h-[60vh] w-full flex-col items-center justify-center px-4 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-3xl text-accent-800">
            <i className="ri-question-line" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">这条瓜料不见了</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已经被下架或改名，回首页看看今天的新线索吧。</p>
          <Link
            to="/"
            className="mt-6 cursor-pointer rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <SiteHeader />
      <main>
        <article>
          <header className="bg-background-100 py-10 md:py-14">
            <div className="mx-auto w-full px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '主题分类', to: '/#categories' },
                  { label: story.category, to: `/category/${story.categorySlug}` },
                  { label: '线索档案' },
                ]}
              />
              <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12">
                <div className="lg:col-span-8">
                  <div className="flex flex-wrap items-center gap-2">
                    <Tag tone={levelTone[story.verdictLevel]}>
                      <i className="ri-shield-check-line" />
                      {story.verdictLevel}
                    </Tag>
                    <Tag tone="neutral">{story.category}</Tag>
                    {story.tags.map((tag) => (
                      <span key={tag} className="text-xs text-foreground-500">
                        #{tag}
                      </span>
                    ))}
                  </div>
                  <h1 className="mt-5 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                    {story.title}
                  </h1>
                  <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                    {story.excerpt}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-background-300 pt-5 text-xs text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-line" />
                      {story.author}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-calendar-line" />
                      更新于 {story.date}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-time-line" />
                      {story.readTime}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-fire-line text-accent-600" />
                      热度 {story.heat.toLocaleString('zh-CN')}
                    </span>
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setBookmarked((v) => !v)}
                      className={`flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2 text-sm transition-colors ${
                        bookmarked
                          ? 'border-primary-300 bg-primary-100 text-primary-800'
                          : 'border-background-300 bg-background-50 text-foreground-700 hover:bg-background-200'
                      }`}
                    >
                      <i className={bookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
                      {bookmarked ? '已收藏这条线索' : '收藏这条线索'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-200"
                    >
                      <i className={copied ? 'ri-check-line' : 'ri-link'} />
                      {copied ? '链接已复制' : '复制链接'}
                    </button>
                  </div>
                </div>

                <div className="lg:col-span-4">
                  <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                    <div className="aspect-[4/3] w-full overflow-hidden bg-background-200">
                      <img
                        src={story.cover}
                        alt={`${story.title} 线索配图`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="bg-background-50 py-12 md:py-16">
            <div className="mx-auto w-full px-4 md:px-6">
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
                <div className="lg:col-span-8">
                  <section id="content">
                    <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                      事件梳理
                    </h2>
                    <div className="mt-5 space-y-4 text-base leading-relaxed text-foreground-700">
                      {story.content.map((paragraph) => (
                        <p key={paragraph.slice(0, 16)}>{paragraph}</p>
                      ))}
                    </div>
                    <div className="mt-6 rounded-lg border-l-4 border-primary-500 bg-accent-100 p-5">
                      <p className="text-xs font-medium text-accent-900">当前判定 · {story.verdictLevel}</p>
                      <p className="mt-2 text-sm leading-relaxed text-accent-900">{story.verdict}</p>
                    </div>
                  </section>

                  <section id="timeline" className="mt-12">
                    <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                      时间线
                    </h2>
                    <ol className="mt-6 space-y-6">
                      {story.timeline.map((node, index) => (
                        <li key={node.time} className="flex gap-4">
                          <span className="flex flex-col items-center">
                            <span
                              className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                                index === 0
                                  ? 'bg-primary-500 text-background-50'
                                  : 'bg-background-200 text-foreground-700'
                              }`}
                            >
                              {index + 1}
                            </span>
                            {index < story.timeline.length - 1 && (
                              <span className="mt-1 w-px flex-1 bg-background-300" />
                            )}
                          </span>
                          <span className="pb-2">
                            <span className="block text-xs font-medium text-primary-700">{node.time}</span>
                            <span className="mt-1 block text-sm leading-relaxed text-foreground-700">
                              {node.text}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ol>
                  </section>

                  <section id="personas" className="mt-12">
                    <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                      人物与账号
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                      以下仅为公开讨论中出现的人物角色，不使用真实姓名，也不指向具体个人隐私。
                    </p>
                    <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {story.personas.map((persona) => (
                        <li
                          key={persona.name}
                          className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
                        >
                          <span className="h-14 w-14 shrink-0 overflow-hidden rounded-full bg-background-200">
                            <img
                              src={persona.avatar}
                              alt={`${persona.name} 示意头像`}
                              className="h-full w-full object-cover object-top"
                            />
                          </span>
                          <span className="min-w-0">
                            <span className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-foreground-950">{persona.name}</span>
                              <Tag tone="primary">{persona.tag}</Tag>
                            </span>
                            <span className="mt-1 block text-xs text-foreground-500">{persona.role}</span>
                            <span className="mt-2 block text-sm leading-relaxed text-foreground-700">
                              {persona.note}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                </div>

                <aside className="lg:col-span-4">
                  <div className="sticky top-24 space-y-5">
                    <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                      <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                        <i className="ri-list-check text-primary-600" />
                        本页目录
                      </h2>
                      <ul className="mt-4 space-y-1">
                        {sections.map((item) => (
                          <li key={item.id}>
                            <a
                              href={`#${item.id}`}
                              className="block cursor-pointer rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-200 hover:text-primary-700"
                            >
                              {item.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                      <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                        <i className="ri-bookmark-line text-accent-600" />
                        相关瓜料
                      </h2>
                      <div className="mt-2">
                        {related.map((item) => (
                          <StoryCard key={item.id} story={item} variant="row" />
                        ))}
                      </div>
                    </div>

                    {category && (
                      <div className="rounded-lg bg-secondary-100 p-5">
                        <h2 className="font-heading text-base font-bold text-secondary-900">
                          所属栏目 · {category.name}
                        </h2>
                        <p className="mt-3 text-sm leading-relaxed text-secondary-900">{category.desc}</p>
                        <Link
                          to={`/category/${category.slug}`}
                          className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-md bg-secondary-600 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-700"
                        >
                          进入栏目
                          <i className="ri-arrow-right-line" />
                        </Link>
                      </div>
                    )}
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </article>

        <section className="border-t border-background-200 bg-background-100 py-14">
          <div className="mx-auto w-full px-4 md:px-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="font-heading text-2xl font-black text-foreground-950">继续追这几条</h2>
              <Link
                to="/#hot"
                className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
              >
                返回热瓜榜 →
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <StoryCard key={item.id} story={item} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}